import type { Ratio } from "@contracts/types";

export type StoredDraftSlide = {
  id: string;
  kind: "image" | "video";
  name: string;
  file?: File;
  url?: string;
  duration?: number;
  sourceRatio?: Ratio;
};

export type StoredDraft = {
  slides: StoredDraftSlide[];
  active: number;
  ratio: Ratio;
  username: string;
  location: string;
  caption: string;
  likes: string;
  avatarFile: File | null;
  avatarUrl?: string | null;
  captionOpen: boolean;
  liked: boolean;
  reposted: boolean;
  saved: boolean;
};

const DATABASE = "rupapreview-drafts";
const STORE = "drafts";
const KEY = "customer-current";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Draft storage could not be opened."));
    request.onblocked = () => reject(new Error("Draft storage is blocked by another tab."));
  });
}

export async function saveDraft(draft: StoredDraft): Promise<void> {
  const db = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE, "readwrite");
      transaction.objectStore(STORE).put(draft, KEY);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error ?? new Error("Draft could not be saved."));
      transaction.onabort = () => reject(transaction.error ?? new Error("Draft saving was interrupted."));
    });
  } finally {
    db.close();
  }
}

export async function loadDraft(): Promise<StoredDraft | null> {
  const db = await openDatabase();
  try {
    return await new Promise<StoredDraft | null>((resolve, reject) => {
      const transaction = db.transaction(STORE, "readonly");
      const request = transaction.objectStore(STORE).get(KEY);
      request.onsuccess = () => resolve((request.result as StoredDraft | undefined) ?? null);
      request.onerror = () => reject(request.error ?? new Error("Draft could not be restored."));
    });
  } finally {
    db.close();
  }
}
